import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

const VIP_PRODUCT_IDS = new Set([
  "prod_UI6NjJW0h3V4X7",
  "prod_UI6NhWBbomvcXa",
  "prod_UI718ekQEaOkpo",
  "prod_UI71FJ7x253tTG",
]);

/**
 * Detects whether the current user's mentoring plan is scheduled to end soon
 * (either Stripe `cancel_at_period_end` on a VIP sub OR manual
 * `client_plans.access_type === 'cancelled'`).
 *
 * Returns the date access ends, or null when there's no pending cancellation.
 */
export function useMentoringCancellation(userId?: string | null) {
  const [cancellationDate, setCancellationDate] = useState<Date | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const detect = async () => {
      try {
        if (userId === null) {
          if (!cancelled) { setCancellationDate(null); setLoading(false); }
          return;
        }
        const uid = userId;
        if (!uid) {
          const { data: { session } } = await supabase.auth.getSession();
          if (!session) {
            if (!cancelled) { setCancellationDate(null); setLoading(false); }
            return;
          }
          return detectForUser(session.user.id);
        }

        return detectForUser(uid);

        async function detectForUser(uid: string) {

          // 1) Manual cancellation flag
          const { data: planRow } = await supabase
            .from("client_plans")
            .select("access_type")
            .eq("user_id", uid)
            .maybeSingle();

          if (planRow?.access_type === "cancelled") {
            const now = new Date();
            const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
            if (!cancelled) { setCancellationDate(endOfMonth); setLoading(false); }
            return;
          }

          // 2) Stripe-side cancellation
          try {
            const { data } = await supabase.functions.invoke("check-subscription");
            const subs: any[] = Array.isArray(data?.subscriptions) ? data.subscriptions : [];
            const vipSub = subs.find((s) => VIP_PRODUCT_IDS.has(s?.product_id));
            if (vipSub?.cancel_at_period_end === true) {
              const iso = vipSub.current_period_end ?? vipSub.cancel_at;
              if (iso && !cancelled) {
                setCancellationDate(new Date(iso));
                setLoading(false);
                return;
              }
            }
          } catch (e) {
            console.warn("[useMentoringCancellation] stripe detect failed", e);
          }

          if (!cancelled) { setCancellationDate(null); setLoading(false); }
        }
      } catch (e) {
        console.warn("[useMentoringCancellation] failed", e);
        if (!cancelled) { setCancellationDate(null); setLoading(false); }
      }
    };
    detect();
    return () => { cancelled = true; };
  }, [userId]);

  return { cancellationDate, loading };
}
